import type { useMainStore } from '@/store'

// issue #822: OpenPixel(WebBeacon)のブラウザ識別子 s1 をサーバ発行のCookieに置き換える。
//
// OpenPixelは s1 を `document.cookie` で書くため、Safari(ITP)では最長7日
// (広告からの着地では24時間)に切り詰められ、流入時と購入時の s1 が別物になる。
// OpenPixelが書いた直後に、Railsが同じ値を `Set-Cookie` で発行し直すことで、
// ITPの上限を受けないCookieに置き換える(値はRails側でも作らない)。
//
// OpenPixel自体は修正しないため、次の挙動に依存している(OpenPixelを変更する場合は要確認):
// - 本体JSの読み込み時に1回だけ s1 を書き直し、その後に `window.opix.process` を定義する
// - SPA遷移では本体JSを読み直さないため、s1 を書き直さない
// そのため、ページ読み込みごとに1回、`process` の出現を待ってから呼ぶ。

export const S1_COOKIE_KEY = '_lb_s1'

const API_PATH = 'supports/generate_cookie_s1'

// OpenPixelはGTM経由で後から読み込まれるため、出現を待つ
const POLL_INTERVAL_MS = 500
const MAX_WAIT_MS = 10000

type MainStore = ReturnType<typeof useMainStore>
type OpixWindow = Window & { opix?: { process?: unknown } }

// OpenPixelの本体JSが読み込まれ、s1 の書き直しが済んでいるか
export function isOpixReady(win: OpixWindow): boolean {
  return typeof win.opix?.process === 'function'
}

export function hasS1Cookie(cookie: string): boolean {
  return cookie.split(';').some(c => c.trim().startsWith(`${S1_COOKIE_KEY}=`))
}

// OpenPixelの読み込みを待ってから、s1 の発行し直しをRailsへ依頼する。
// 待ち時間内に読み込まれなければ何もしない(GTMが無効な環境など)。
export function refreshS1CookieAfterOpix(store: MainStore, win: OpixWindow = window): void {
  let waited = 0
  const timer = win.setInterval(() => {
    waited += POLL_INTERVAL_MS
    if (isOpixReady(win)) {
      win.clearInterval(timer)
      if (!hasS1Cookie(win.document.cookie)) return
      // 分析用の副作用なので、失敗しても画面には影響させない(未処理のrejectionを防ぐ)。
      // 失敗しても次のページ読み込みで再試行される。
      store.dispatch('apiGet', API_PATH).catch(() => undefined)
    } else if (waited >= MAX_WAIT_MS) {
      win.clearInterval(timer)
    }
  }, POLL_INTERVAL_MS)
}
